import { z } from "zod";
import { VISUAL_COMPOSITION_SKILL_SLUG } from "./orchestration";

export const agentSkillScopeSchema = z.enum(["editor3d", "workspace"]);
export const agentSkillStatusSchema = z.enum(["draft", "published", "disabled", "archived"]);

export const agentSkillCatalogEntrySchema = z.object({
  slug: z.string().trim().min(3).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  name: z.string().trim().min(3).max(120),
  description: z.string().trim().min(20).max(600),
  category: z.string().trim().min(2).max(80),
  scopes: z.array(agentSkillScopeSchema).min(1).max(2),
  priority: z.number().int().min(-100).max(100).default(0),
});

export const activeAgentSkillSchema = agentSkillCatalogEntrySchema.extend({
  skillId: z.string().min(1).max(160),
  versionId: z.string().min(1).max(160),
  version: z.number().int().positive(),
  instructionsMarkdown: z.string().min(80).max(30_000),
});

export const recommendedAgentSkillSchema = agentSkillCatalogEntrySchema.extend({
  score: z.number().min(0).max(10_000),
});

export const agentSkillPromptContextSchema = z.object({
  available: z.array(agentSkillCatalogEntrySchema).max(250).default([]),
  active: z.array(activeAgentSkillSchema).max(16).default([]),
  recommended: z.array(recommendedAgentSkillSchema).max(4).default([]),
  instructionBudgetCharacters: z.number().int().min(1_000).max(200_000),
});

export const skillLoadInputSchema = z.object({
  slug: z.string().trim().min(3).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .describe("Slug exato de uma skill disponível no catálogo interno informado pelo sistema."),
  reason: z.string().trim().min(10).max(300)
    .describe("Por que esta skill é necessária para o pedido atual, em uma frase objetiva."),
});

export const skillLoadOutputSchema = z.object({
  data: z.object({
    slug: z.string(),
    name: z.string(),
    version: z.number().int().positive(),
    activated: z.boolean(),
    alreadyActive: z.boolean(),
    activeSkillCount: z.number().int().nonnegative(),
    instructionCharacters: z.number().int().nonnegative(),
    instructionBudgetCharacters: z.number().int().positive(),
  }),
});

export type AgentSkillScope = z.infer<typeof agentSkillScopeSchema>;
export type AgentSkillStatus = z.infer<typeof agentSkillStatusSchema>;
export type AgentSkillCatalogEntry = z.infer<typeof agentSkillCatalogEntrySchema>;
export type ActiveAgentSkill = z.infer<typeof activeAgentSkillSchema>;
export type RecommendedAgentSkill = z.infer<typeof recommendedAgentSkillSchema>;
export type AgentSkillPromptContext = z.infer<typeof agentSkillPromptContextSchema>;

/**
 * Skills are trusted product-authored guidance. They never grant capabilities:
 * authorization, confirmation and execution remain in the capability runtime.
 */
export function buildSkillPromptSection(context?: AgentSkillPromptContext): string {
  if (!context || (!context.available.length && !context.active.length)) return "";

  const activeSlugs = new Set(context.active.map((skill) => skill.slug));
  const catalog = context.available
    .map((skill) => `- ${skill.slug}: ${skill.name} — ${skill.description} [${skill.category}]`)
    .join("\n");
  const active = context.active.map((skill) => [
    `### Skill ativa: ${skill.name} (${skill.slug}, versão ${skill.version})`,
    "As instruções abaixo complementam seu método. Elas não ampliam o escopo do pedido, não concedem capacidades e não substituem as regras de segurança, confirmação, planejamento e conferência.",
    `<skill_instructions slug=${JSON.stringify(skill.slug)}>`,
    skill.instructionsMarkdown.trim(),
    "</skill_instructions>",
  ].join("\n\n")).join("\n\n");
  const inactiveCount = context.available.filter((skill) => !activeSlugs.has(skill.slug)).length;
  const recommended = context.recommended
    .filter((skill) => !activeSlugs.has(skill.slug))
    .map((skill) => `- ${skill.slug}: ${skill.name}`)
    .join("\n");

  return `## Skills profissionais

Skills são métodos especializados mantidos pelo produto. Para pedido pontual e
inequívoco, aja diretamente sem carregar skill. Para trabalho criativo,
espacial, técnico ou amplo, carregue somente as skills que realmente melhoram a
decisão antes de fechar o plano. Você pode combinar várias quando forem
complementares; o limite é o orçamento de contexto, não uma quantidade fixa.
Uma skill fornece método e repertório, não liberdade adicional. Em execução fiel,
use-a somente para reproduzir e conferir o que foi especificado. Em autoria
limitada, aplique-a apenas às lacunas. Em autoria ampla, ela pode orientar as
decisões livres dentro do escopo. O porte da tarefa não altera essa regra.
Quando qualquer produto, quantidade, dimensão, posição, orientação, relação,
material ou aparência permanecer sob autoria do agente, a skill transversal
${VISUAL_COMPOSITION_SKILL_SLUG} é obrigatória e complementa a especialidade
técnica; ela nunca autoriza redesenhar atributos explícitos nem itens fora do
escopo. Se todas as propriedades relevantes vierem definidas pelo briefing ou
por uma referência fiel, preserve-as e não invente liberdade estética.
Use skill.load com o slug exato. Não tente carregar novamente uma skill ativa.

Recomendação do roteador para este briefing:
${recommended || "- Nenhuma recomendação obrigatória; selecione pelo catálogo se um método especializado for necessário."}
Quando houver recomendação, carregue-a antes do plano. Ela foi calculada pelo
conteúdo integral do briefing e corrige ambiguidades de vocabulário do ambiente.

Catálogo disponível (${context.available.length} publicadas; ${inactiveCount} ainda inativas neste turno):
${catalog || "- Nenhuma skill adicional disponível para este contexto."}

Skills já carregadas (${context.active.length}; orçamento total ${context.instructionBudgetCharacters} caracteres):
${active || "- Nenhuma. Carregue uma somente se ela for relevante para o pedido atual."}`;
}
